import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import type { GetBuildEndpointV1ConversationsConversationIdBuildsBuildIdGetApiResponse } from '~/api/hermes/typedefs.autogen';
import type {
  AiWorkflowBuild,
  AiWorkflowBuildStatus,
} from '~/redux/slices/ai-workflow-builds/types';

import { Tags } from '~/api/k2/tags.autogen';
import { usePusherChannel } from '~/common/contexts/PusherContext';
import { PusherEventType } from '~/enums/PusherEventType';
import { hermes } from '~/redux/api/hermes';
import { k2 } from '~/redux/api/k2';
import { slices } from '~/redux/slices';

const POLL_INTERVAL_MS = 30_000;

const ACTIVE_STATUSES = new Set<AiWorkflowBuildStatus>([
  'pending_scan',
  'queued',
  'running',
]);

// A build is a RUN (``ai_builds``), so its status is read off the run itself,
// not an assistant message (there is none until it SUCCEEDS). The orchestrator
// progression PENDING_SCAN -> QUEUED -> INVOKING -> RUNNING keeps the tray
// spinner; SUCCEEDED/FAILED are terminal.
const FE_STATUS_BY_RUN_STATUS: Record<
  string,
  AiWorkflowBuildStatus | undefined
> = {
  PENDING_SCAN: 'pending_scan',
  QUEUED: 'queued',
  INVOKING: 'running',
  RUNNING: 'running',
  SUCCEEDED: 'ready',
  FAILED: 'failed',
};

type BuildSummary =
  GetBuildEndpointV1ConversationsConversationIdBuildsBuildIdGetApiResponse;

export type BuildStatusPush = {
  conversation_id: string;
  build_id: string;
  result_message_id?: string | null;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED' | (string & {});
  workflow_id?: string | null;
};

type BuildUpdate = {
  status: AiWorkflowBuildStatus;
  workflow_id?: string | null;
  error?: string | null;
};

const stringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;

// The build run is the source of truth. SUCCEEDED/FAILED are terminal, so they
// always resolve the tray item. A SUCCEEDED run is marked ready even when it
// carries no workflow_id -- the tray simply hides "Open Workflow" until one is
// known (see AiWorkflowBuildsNotificationSource). Returning null here on SUCCEEDED would keep
// polling forever for a run that never exposes a workflow_id, so a missed Pusher
// push could leave the tray spinning indefinitely.
const updateFromBuild = (summary: BuildSummary): BuildUpdate | null => {
  const feStatus = FE_STATUS_BY_RUN_STATUS[summary.status.toUpperCase()];
  if (!feStatus) return null;
  if (feStatus === 'ready') {
    return { status: 'ready', workflow_id: stringValue(summary.workflow_id) };
  }
  if (feStatus === 'failed') {
    return { status: 'failed', error: stringValue(summary.error) };
  }
  return { status: feStatus };
};

// The Pusher push lets us react on the happy path without a fetch. It carries
// no error text, so on FAILED we fall through to a poll to surface the run's
// error; same for a SUCCEEDED ping that arrives before workflow_id is set.
const updateFromPush = (event: BuildStatusPush): BuildUpdate | null => {
  const status = event.status.toUpperCase();
  if (status === 'RUNNING') {
    return { status: 'running' };
  }
  if (status === 'SUCCEEDED' && event.workflow_id) {
    return { status: 'ready', workflow_id: event.workflow_id };
  }
  return null;
};

export const useAiWorkflowBuildMonitor = (build: AiWorkflowBuild): void => {
  const dispatch = useDispatch();
  const { conversation_id, build_id, status } = build;
  const [getBuild] =
    hermes.endpoints.getBuildEndpointV1ConversationsConversationIdBuildsBuildIdGet.useLazyQuery();
  const isActive = ACTIVE_STATUSES.has(status);
  const channelName = isActive ? `private-clme-ai-${conversation_id}` : null;
  const { channel } = usePusherChannel(channelName);

  const dispatchUpdate = useCallback(
    (update: BuildUpdate) => {
      dispatch(
        slices.aiWorkflowBuilds.actions.updateBuild({
          build_id,
          ...update,
        }),
      );
      if (update.status === 'ready') {
        dispatch(k2.util.invalidateTags([{ type: Tags.Workflow, id: 'LIST' }]));
      }
    },
    [dispatch, build_id],
  );

  const pollBuildStatus = useCallback(async () => {
    const summary = await getBuild({
      conversationId: conversation_id,
      buildId: build_id,
    }).unwrap();
    const update = updateFromBuild(summary);
    if (update) dispatchUpdate(update);
  }, [build_id, conversation_id, dispatchUpdate, getBuild]);

  useEffect(() => {
    if (!channel || !isActive) return;

    const handleBuildStatus = (event: BuildStatusPush) => {
      if (
        event.conversation_id !== conversation_id ||
        event.build_id !== build_id
      ) {
        return;
      }

      const update = updateFromPush(event);
      if (update) {
        dispatchUpdate(update);
        return;
      }

      pollBuildStatus().catch(() => {});
    };

    channel.bind(PusherEventType.BuildStatus, handleBuildStatus);
    return () => {
      channel.unbind(PusherEventType.BuildStatus, handleBuildStatus);
    };
  }, [
    build_id,
    channel,
    conversation_id,
    dispatchUpdate,
    isActive,
    pollBuildStatus,
  ]);

  useEffect(() => {
    if (!isActive) return;

    pollBuildStatus().catch(() => {});

    const intervalId = window.setInterval(() => {
      pollBuildStatus().catch(() => {});
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [isActive, pollBuildStatus]);
};
